import React, { useState } from 'react';
import { ArrowsRightLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    facilities: any[];
}

const initialForm = {
    facilityId: '',
    cropType: 'compost',
    itemType: 'compost',
    quantityKg: '',
    reason: 'transfer',
    sourceType: 'warehouse',
    sourceId: '',
    destinationType: 'distribution_point',
    destinationId: '',
    farmerId: '',
    batchReference: '',
    temperature: '',
    humidity: '',
    notes: ''
};

const RecordInternalMovementModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, facilities }) => {
    const [formData, setFormData] = useState(initialForm);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSubmit(formData);
            setFormData(initialForm);
            onClose();
        } catch (error) {
            console.error('Failed to record internal movement:', error);
            alert(error instanceof Error ? error.message : 'Failed to record internal movement');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="bg-gradient-to-r from-indigo-600 to-sky-600 px-6 py-5 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <ArrowsRightLeftIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">Record Internal Movement</h2>
                                <p className="text-indigo-100 text-sm">Track transfer flow inside operations chain</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Facility *</label>
                            <select
                                value={formData.facilityId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, facilityId: e.target.value }))}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="">Select facility</option>
                                {facilities.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Item Type *</label>
                            <select
                                value={formData.itemType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, itemType: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="compost">Compost</option>
                                <option value="produce">Produce</option>
                                <option value="fertilizer">Fertilizer</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Crop Type *</label>
                            <input
                                type="text"
                                value={formData.cropType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cropType: e.target.value }))}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="e.g. compost, avocado"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity (kg) *</label>
                            <input
                                type="number"
                                value={formData.quantityKg}
                                onChange={(e) => setFormData((prev) => ({ ...prev, quantityKg: e.target.value }))}
                                required
                                min="0"
                                step="0.01"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Reason *</label>
                            <select
                                value={formData.reason}
                                onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="transfer">Transfer</option>
                                <option value="compost_distribution">Compost Distribution</option>
                                <option value="sorting">Sorting</option>
                                <option value="processing">Processing</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Source Type *</label>
                            <select
                                value={formData.sourceType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, sourceType: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="warehouse">Warehouse</option>
                                <option value="batch">Batch</option>
                                <option value="processing_site">Processing Site</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Source ID</label>
                            <input
                                type="number"
                                value={formData.sourceId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, sourceId: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Destination Type *</label>
                            <select
                                value={formData.destinationType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, destinationType: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="distribution_point">Distribution Point</option>
                                <option value="warehouse">Warehouse</option>
                                <option value="field">Field Application</option>
                                <option value="processing_site">Processing Site</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Destination ID</label>
                            <input
                                type="number"
                                value={formData.destinationId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, destinationId: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Farmer ID (optional)</label>
                            <input
                                type="number"
                                value={formData.farmerId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, farmerId: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Batch Reference</label>
                            <input
                                type="text"
                                value={formData.batchReference}
                                onChange={(e) => setFormData((prev) => ({ ...prev, batchReference: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Temp (°C)</label>
                            <input
                                type="number"
                                value={formData.temperature}
                                onChange={(e) => setFormData((prev) => ({ ...prev, temperature: e.target.value }))}
                                step="0.1"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Humidity (%)</label>
                            <input
                                type="number"
                                value={formData.humidity}
                                onChange={(e) => setFormData((prev) => ({ ...prev, humidity: e.target.value }))}
                                step="0.1"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-sky-600 text-white rounded-lg hover:from-indigo-700 hover:to-sky-700 transition font-medium shadow-lg disabled:opacity-50"
                        >
                            {loading ? 'Recording...' : 'Record Movement'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecordInternalMovementModal;
